import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    Upload,
    Download,
    X,
    FileText,
    AlertCircle,
    CheckCircle,
    Loader2,
    AlertTriangle,
    FileSpreadsheet,
    Copy
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import candidateService from '../../../api/services/candidateService';
import jobService from '../../../api/services/jobService';
import departmentRoleService from '../../../api/services/departmentRoleService';
import { skillService } from '../../../api/crm/config/configService';
import { toast } from 'react-hot-toast';

const STAGE_VALUES = [
    'applied',
    'screening',
    'shortlisted',
    'interview',
    'interviewed',
    'offer',
    'offered',
    'hired',
    'rejected',
];

const NOTICE_PERIOD_VALUES = [
    'immediate',
    '15-days',
    '30-days',
    '45-days',
    '60-days',
    '90-days',
];

/**
 * CandidateBulkUploadModal - Modal for bulk uploading candidates from CSV
 */
const CandidateBulkUploadModal = ({ isOpen, onClose, onSuccess, jobs = [], departments = [] }) => {
    const [step, setStep] = useState('upload'); // 'upload', 'preview', 'uploading', 'result'
    const [file, setFile] = useState(null);
    const [parsedCandidates, setParsedCandidates] = useState([]);
    const [validationResult, setValidationResult] = useState(null);
    const [uploadResult, setUploadResult] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [skipDuplicates, setSkipDuplicates] = useState(true);
    const [loadingInstructions, setLoadingInstructions] = useState(false);
    const [instructionValues, setInstructionValues] = useState({
        jobs: [],
        departments: [],
        skills: [],
        sources: [],
    });
    const fileInputRef = useRef(null);

    const uniqueSorted = (arr = []) => Array.from(new Set(arr.filter(Boolean).map((v) => String(v).trim()))).sort((a, b) => a.localeCompare(b));

    useEffect(() => {
        if (!isOpen) return;

        const loadInstructionValues = async () => {
            setLoadingInstructions(true);
            try {
                const [jobResult, deptResult, skillResult, sourceResult] = await Promise.all([
                    jobService.getJobs({ page: 1, limit: 1000, status: 'active,published' }),
                    departmentRoleService.getDepartments({ isActive: 'true', paginated: 'false', limit: 1000 }),
                    skillService.getAll({ isActive: true }),
                    jobService.getPublicSources(),
                ]);

                const jobsData = Array.isArray(jobResult?.data?.jobs)
                    ? jobResult.data.jobs
                    : Array.isArray(jobResult?.data?.data)
                        ? jobResult.data.data
                        : Array.isArray(jobResult?.data)
                            ? jobResult.data
                            : [];
                const jobTitles = uniqueSorted(jobsData.map((job) => job?.title || job?.jobTitle || ''));

                const deptPayload = Array.isArray(deptResult)
                    ? deptResult
                    : Array.isArray(deptResult?.data)
                        ? deptResult.data
                        : Array.isArray(deptResult?.data?.departments)
                            ? deptResult.data.departments
                            : Array.isArray(deptResult?.departments)
                                ? deptResult.departments
                                : [];
                const departmentNames = uniqueSorted(
                    deptPayload.map((dept) => {
                        if (typeof dept === 'string') return dept;
                        return dept?.name || dept?.departmentName || dept?.title || dept?.department || '';
                    }),
                );

                const skillsData = Array.isArray(skillResult?.data)
                    ? skillResult.data
                    : Array.isArray(skillResult?.data?.skills)
                        ? skillResult.data.skills
                        : Array.isArray(skillResult)
                            ? skillResult
                            : [];
                const skillNames = uniqueSorted(skillsData.map((skill) => (typeof skill === 'string' ? skill : skill?.name || skill?.label || '')));

                const sourcesData = Array.isArray(sourceResult?.data)
                    ? sourceResult.data
                    : Array.isArray(sourceResult)
                        ? sourceResult
                        : [];
                const sourceValues = uniqueSorted(
                    sourcesData.map((item) => {
                        if (typeof item === 'string') return item;
                        return item?.value || item?.label || item?.name || item?.title || '';
                    }),
                );

                setInstructionValues({
                    jobs: jobTitles,
                    departments: departmentNames,
                    skills: skillNames,
                    sources: sourceValues,
                });
            } catch (error) {
                setInstructionValues({ jobs: [], departments: [], skills: [], sources: [] });
                toast.error('Unable to load all CSV allowed values from backend');
            } finally {
                setLoadingInstructions(false);
            }
        };

        loadInstructionValues();
    }, [isOpen]);

    // Reset state when modal closes
    const handleClose = useCallback(() => {
        setStep('upload');
        setFile(null);
        setParsedCandidates([]);
        setValidationResult(null);
        setUploadResult(null);
        setIsUploading(false);
        setIsValidating(false);
        onClose();
    }, [onClose]);

    // Handle file selection
    const handleFileChange = async (e) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        // Validate file type
        const validTypes = ['text/csv', 'application/vnd.ms-excel', '.csv'];
        const isValidType = validTypes.some(type =>
            selectedFile.type === type || selectedFile.name.endsWith('.csv')
        );

        if (!isValidType) {
            toast.error('Please upload a CSV file');
            return;
        }

        // Validate file size (max 5MB)
        if (selectedFile.size > 5 * 1024 * 1024) {
            toast.error('File size must be less than 5MB');
            return;
        }

        setFile(selectedFile);

        // Read and parse the file
        const reader = new FileReader();
        reader.onload = async (event) => {
            const csvContent = event.target?.result;
            if (typeof csvContent !== 'string') return;

            const result = candidateService.parseCSV(csvContent);
            if (!result.success) {
                toast.error(result.error || 'Failed to parse CSV');
                setFile(null);
                return;
            }

            if (result.candidates.length === 0) {
                toast.error('No valid candidate data found in CSV');
                setFile(null);
                return;
            }

            /* Limit removed as per user request to handle 1000+ candidates */

            setParsedCandidates(result.candidates);
            setStep('preview');

            // Auto-validate
            await validateCandidates(result.candidates);
        };

        reader.onerror = () => {
            toast.error('Failed to read file');
            setFile(null);
        };

        reader.readAsText(selectedFile);
    };

    // Validate parsed candidates
    const validateCandidates = async (candidates) => {
        setIsValidating(true);
        try {
            const result = await candidateService.validateBulkUpload(candidates);
            setValidationResult(result);
        } catch (error) {
            toast.error('Validation failed: ' + (error.message || 'Unknown error'));
        } finally {
            setIsValidating(false);
        }
    };

    // Download template
    const handleDownloadTemplate = async () => {
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;

        const filename = `candidate_bulk_upload_template_${day}-${month}-${year}_${String(hours).padStart(2, '0')}-${minutes}_${ampm}.csv`;
        const templateJobs = (instructionValues.jobs.length > 0
            ? instructionValues.jobs
            : jobs.map((j) => j?.label || j?.value || j)
        ).map((title) => ({ label: title }));

        const templateDepartments = (instructionValues.departments.length > 0
            ? instructionValues.departments
            : departments.map((d) => d?.label || d?.value || d)
        ).map((name) => ({ label: name }));

        const result = await candidateService.downloadBulkUploadTemplate(filename, {
            jobs: templateJobs,
            departments: templateDepartments,
        });
        if (!result.success) {
            toast.error(result.error || 'Failed to download template');
        } else {
            toast.success('Template downloaded');
        }
    };

    // Handle drag and drop
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const droppedFile = e.dataTransfer?.files?.[0];
        if (droppedFile) {
            const event = { target: { files: [droppedFile] } };
            handleFileChange(event);
        }
    };

    // Perform the actual upload with chunking for large datasets
    const handleUpload = async () => {
        if (parsedCandidates.length === 0) return;

        setIsUploading(true);
        setStep('uploading');

        try {
            // Determine candidates to upload (excluding duplicates if skipDuplicates is true)
            let candidatesToUpload = [...parsedCandidates];
            if (skipDuplicates && validationResult?.data?.duplicates) {
                const duplicateEmails = new Set(validationResult.data.duplicates.map(d => d.email?.toLowerCase()));
                candidatesToUpload = candidatesToUpload.filter(c => !duplicateEmails.has(c.email?.toLowerCase()));
            }

            // Identify rows with errors if possible, to skip them
            if (validationResult?.data?.errors) {
                const errorRows = new Set(validationResult.data.errors.map(e => e.row - 1)); // row is 1-indexed in validation
                candidatesToUpload = candidatesToUpload.filter((_, idx) => !errorRows.has(idx));
            }

            if (candidatesToUpload.length === 0) {
                toast.error('No valid candidates left to upload after filtering duplicates/errors');
                setStep('preview');
                setIsUploading(false);
                return;
            }

            // Chunking strategy for large datasets (100 candidates per batch)
            const CHUNK_SIZE = 100;
            const chunks = [];
            for (let i = 0; i < candidatesToUpload.length; i += CHUNK_SIZE) {
                chunks.push(candidatesToUpload.slice(i, i + CHUNK_SIZE));
            }

            let totalCreated = 0;
            let totalUpdated = 0;
            let totalFailed = 0;
            const allErrors = [];

            for (let i = 0; i < chunks.length; i++) {
                // Update toast with progress for large uploads
                if (chunks.length > 1) {
                    toast.loading(`Uploading batch ${i + 1} of ${chunks.length}...`, { id: 'upload-progress' });
                }

                const result = await candidateService.bulkUploadCandidates(chunks[i], { skipDuplicates });
                
                if (result.success) {
                    totalCreated += result.data?.created || 0;
                    totalUpdated += result.data?.updated || 0;
                    totalFailed += result.data?.failed || 0;
                    if (result.data?.errors) {
                        allErrors.push(...result.data.errors.map(err => ({ ...err, row: err.row + (i * CHUNK_SIZE) })));
                    }
                } else {
                    totalFailed += chunks[i].length;
                    allErrors.push({ batch: i + 1, error: result.error || 'Batch failed' });
                }
            }

            toast.dismiss('upload-progress');

            const finalResult = {
                success: totalCreated > 0 || totalUpdated > 0,
                message: `Bulk upload completed: ${totalCreated} created, ${totalUpdated} updated, ${totalFailed} failed`,
                data: {
                    total: candidatesToUpload.length,
                    created: totalCreated,
                    updated: totalUpdated,
                    failed: totalFailed,
                    errors: allErrors,
                    skipped: (parsedCandidates.length - candidatesToUpload.length)
                }
            };

            setUploadResult(finalResult);
            setStep('result');

            if (finalResult.success) {
                toast.success(`Successfully uploaded ${totalCreated + totalUpdated} candidates`);
                if (onSuccess) onSuccess();
            } else {
                toast.error('Upload failed or no candidates were created');
            }
        } catch (error) {
            toast.dismiss('upload-progress');
            toast.error(error.message || 'Upload failed');
            setUploadResult({
                success: false,
                error: error.message || 'Upload failed'
            });
            setStep('result');
        } finally {
            setIsUploading(false);
        }
    };

    // Go back to previous step
    const handleBack = () => {
        if (step === 'preview') {
            setStep('upload');
            setFile(null);
            setParsedCandidates([]);
            setValidationResult(null);
        } else if (step === 'result') {
            handleClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-brand/10 rounded-lg">
                            <Upload className="h-5 w-5 text-brand" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Bulk Upload Candidates</h2>
                            <p className="text-sm text-gray-500">
                                {step === 'upload' && 'Upload a CSV file with candidate data'}
                                {step === 'preview' && (() => {
                                    const errorCount = validationResult?.data?.failed || 0;
                                    const duplicateCount = validationResult?.data?.duplicates?.length || 0;
                                    const readyCount = Math.max(0, parsedCandidates.length - errorCount - duplicateCount);
                                    if (readyCount > 0) {
                                        return `${readyCount} of ${parsedCandidates.length} candidates ready for upload`;
                                    } else {
                                        return `${parsedCandidates.length} candidates parsed, none ready for upload`;
                                    }
                                })()}
                                {step === 'uploading' && 'Uploading candidates...'}
                                {step === 'result' && 'Upload complete'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-blue-100/60 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Upload Step */}
                    {step === 'upload' && (
                        <div className="space-y-6">
                            {/* Drag & Drop Zone */}
                            <div
                                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-brand hover:bg-brand/5 transition-all cursor-pointer"
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileChange}
                                    className="hidden"
                                />
                                <FileSpreadsheet className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-lg font-medium text-gray-700 mb-2">
                                    Drag & drop your CSV file here
                                </p>
                                <p className="text-sm text-gray-500 mb-4">
                                    or click to browse (Handles 1000+ candidates, Max 5MB)
                                </p>
                                <Button variant="outline" icon={<Upload className="h-4 w-4" />}>
                                    Select File
                                </Button>
                            </div>

                            {/* Template Download */}
                            <div className="bg-blue-50 rounded-xl p-4 flex items-start space-x-4">
                                <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                                    <FileText className="h-5 w-5 text-[#0F2942]" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-sm font-medium text-blue-900 mb-1">
                                        Need a template?
                                    </h4>
                                    <p className="text-sm text-[#0B2033] mb-3">
                                        Download our CSV template with all the required fields and sample data.
                                    </p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        icon={<Download className="h-4 w-4" />}
                                        onClick={handleDownloadTemplate}
                                        className="border-blue-300 text-[#0B2033] hover:bg-blue-100"
                                    >
                                        Download Template
                                    </Button>
                                </div>
                            </div>

                            {/* Field Reference */}
                            <div className="bg-gray-50 rounded-xl p-4">
                                <h4 className="text-sm font-medium text-gray-900 mb-3">Field Guidelines</h4>
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Required</p>
                                            <ul className="text-sm text-gray-700 space-y-1">
                                                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></span>firstName, lastName</li>
                                                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></span>email, phone</li>
                                                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></span>jobTitle</li>
                                                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></span>department</li>
                                                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></span>experience</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Other Fields</p>
                                            <ul className="text-sm text-gray-700 space-y-1">
                                                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></span>Address (city, country, etc.)</li>
                                                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></span>Education, Company, Salary</li>
                                                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></span>Social (LinkedIn, GitHub)</li>
                                                <li className="flex items-center"><span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></span>Skills & Education (semicolon ;)</li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="pt-2 border-t border-gray-200">
                                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Data Guide</p>
                                        <div className="space-y-2">
                                            <p className="text-xs text-gray-600">
                                                Use exact values from the lists below for restricted CSV fields. For multi-value fields like
                                                <strong> skills</strong>, <strong>education</strong>, <strong>university</strong>, and
                                                <strong> graduationYear</strong>, separate values with <strong>semicolon ( ; )</strong>.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="pt-2 border-t border-gray-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-xs font-semibold text-gray-500 uppercase">Allowed CSV Values</p>
                                            {loadingInstructions && (
                                                <span className="text-[11px] text-gray-500">Loading from backend...</span>
                                            )}
                                        </div>

                                        <div className="space-y-3">
                                            <div className="p-2 bg-indigo-50 border border-indigo-100 rounded-md">
                                                <p className="text-[10px] font-bold text-indigo-700 uppercase mb-1">Stage</p>
                                                <p className="text-xs text-indigo-800 leading-relaxed font-medium">
                                                    {STAGE_VALUES.join(' • ')}
                                                </p>
                                            </div>

                                            <div className="p-2 bg-green-50 border border-green-100 rounded-md">
                                                <p className="text-[10px] font-bold text-green-700 uppercase mb-1">Job Title (active/published jobs)</p>
                                                <p className="text-xs text-green-800 leading-relaxed font-medium break-words">
                                                    {instructionValues.jobs.length > 0 ? instructionValues.jobs.join(' • ') : 'No values available'}
                                                </p>
                                            </div>

                                            <div className="p-2 bg-blue-50 border border-blue-100 rounded-md">
                                                <p className="text-[10px] font-bold text-[#0B2033] uppercase mb-1">Department (active only)</p>
                                                <p className="text-xs text-blue-800 leading-relaxed font-medium break-words">
                                                    {instructionValues.departments.length > 0 ? instructionValues.departments.join(' • ') : 'No values available'}
                                                </p>
                                            </div>

                                            <div className="p-2 bg-amber-50 border border-amber-100 rounded-md">
                                                <p className="text-[10px] font-bold text-amber-700 uppercase mb-1">Notice Period</p>
                                                <p className="text-xs text-amber-800 leading-relaxed font-medium">
                                                    {NOTICE_PERIOD_VALUES.join(' • ')}
                                                </p>
                                            </div>

                                            <div className="p-2 bg-cyan-50 border border-cyan-100 rounded-md">
                                                <p className="text-[10px] font-bold text-cyan-700 uppercase mb-1">Skills (active only)</p>
                                                <p className="text-xs text-cyan-800 leading-relaxed font-medium break-words">
                                                    {instructionValues.skills.length > 0 ? instructionValues.skills.join(' • ') : 'No values available'}
                                                </p>
                                            </div>

                                            <div className="p-2 bg-purple-50 border border-purple-100 rounded-md">
                                                <p className="text-[10px] font-bold text-purple-700 uppercase mb-1">Source (public sources)</p>
                                                <p className="text-xs text-purple-800 leading-relaxed font-medium break-words">
                                                    {instructionValues.sources.length > 0 ? instructionValues.sources.join(' • ') : 'No values available'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Preview Step */}
                    {step === 'preview' && (
                        <div className="space-y-6">
                            {/* File Info */}
                            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                                <div className="flex items-center space-x-3">
                                    <FileSpreadsheet className="h-8 w-8 text-brand" />
                                    <div>
                                        <p className="font-medium text-gray-900">{file?.name}</p>
                                        <p className="text-sm text-gray-500">{parsedCandidates.length} candidates parsed</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setFile(null);
                                        setParsedCandidates([]);
                                        setValidationResult(null);
                                        setStep('upload');
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            {/* Validation Status */}
                            {isValidating ? (
                                <div className="flex items-center justify-center p-8">
                                    <Loader2 className="h-8 w-8 animate-spin text-brand" />
                                    <span className="ml-3 text-gray-600">Validating candidates...</span>
                                </div>
                            ) : validationResult && (() => {
                                const totalItems = parsedCandidates.length;
                                const errorCount = validationResult.data?.failed || 0;
                                const duplicateCount = validationResult.data?.duplicates?.length || 0;
                                const readyToUpload = Math.max(0, totalItems - errorCount - duplicateCount);

                                return (
                                    <div className="space-y-4">
                                        {/* Summary */}
                                        <div className="grid grid-cols-4 gap-3">
                                            <div className="bg-blue-50 rounded-lg p-3 text-center">
                                                <p className="text-xl font-bold text-[#0B2033]">
                                                    {totalItems}
                                                </p>
                                                <p className="text-xs text-[#0F2942]">Total Parsed</p>
                                            </div>
                                            <div className="bg-red-50 rounded-lg p-3 text-center">
                                                <AlertCircle className="h-5 w-5 text-red-600 mx-auto mb-1" />
                                                <p className="text-xl font-bold text-red-700">
                                                    {errorCount}
                                                </p>
                                                <p className="text-xs text-red-600">Errors</p>
                                            </div>
                                            <div className="bg-yellow-50 rounded-lg p-3 text-center">
                                                <AlertTriangle className="h-5 w-5 text-yellow-600 mx-auto mb-1" />
                                                <p className="text-xl font-bold text-yellow-700">
                                                    {duplicateCount}
                                                </p>
                                                <p className="text-xs text-yellow-600">Duplicates</p>
                                            </div>
                                            <div className={`rounded-lg p-3 text-center ${readyToUpload > 0 ? 'bg-green-50' : 'bg-gray-50'}`}>
                                                <CheckCircle className={`h-5 w-5 mx-auto mb-1 ${readyToUpload > 0 ? 'text-green-600' : 'text-gray-400'}`} />
                                                <p className={`text-xl font-bold ${readyToUpload > 0 ? 'text-green-700' : 'text-gray-500'}`}>
                                                    {readyToUpload}
                                                </p>
                                                <p className={`text-xs ${readyToUpload > 0 ? 'text-green-600' : 'text-gray-500'}`}>Ready to Upload</p>
                                            </div>
                                        </div>

                                        {/* Errors */}
                                        {validationResult.data?.errors?.length > 0 && (
                                            <div className="bg-red-50 rounded-lg p-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h4 className="text-sm font-medium text-red-800">Validation Errors</h4>
                                                    <button
                                                        onClick={() => {
                                                            const allErrors = validationResult.data.errors.join('\n');
                                                            navigator.clipboard.writeText(allErrors);
                                                            toast.success(`Copied ${validationResult.data.errors.length} errors to clipboard`);
                                                        }}
                                                        className="flex items-center space-x-1 text-xs text-red-600 hover:text-red-800 bg-red-100 hover:bg-red-200 px-2 py-1 rounded transition-colors"
                                                        title="Copy all errors"
                                                    >
                                                        <Copy className="h-3 w-3" />
                                                        <span>Copy All</span>
                                                    </button>
                                                </div>
                                                <div className="max-h-32 overflow-y-auto space-y-2">
                                                    {validationResult.data.errors.slice(0, 10).map((error, idx) => (
                                                        <div key={idx} className="text-sm text-red-700 bg-red-100/50 p-2 rounded border border-red-200">
                                                            <span className="font-bold mr-1">Row {error.row}:</span>
                                                            {Array.isArray(error.errors) ? error.errors.join(', ') : (error.error || String(error))}
                                                        </div>
                                                    ))}
                                                    {validationResult.data.errors.length > 10 && (
                                                        <p className="text-sm text-red-600 font-medium px-2">
                                                            ... and {validationResult.data.errors.length - 10} more errors
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Skip Duplicates Option */}
                                        <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                                            <input
                                                type="checkbox"
                                                id="skipDuplicates"
                                                checked={skipDuplicates}
                                                onChange={(e) => setSkipDuplicates(e.target.checked)}
                                                className="h-4 w-4 text-brand rounded border-gray-300 focus:ring-brand"
                                            />
                                            <label htmlFor="skipDuplicates" className="text-sm text-gray-700">
                                                Skip duplicate candidates and continue with upload
                                            </label>
                                        </div>

                                        {/* Preview Table */}
                                        <div className="border rounded-lg overflow-hidden">
                                            <div className="bg-gray-50 px-4 py-2 border-b">
                                                <h4 className="text-sm font-medium text-gray-900">Preview (First 5 candidates)</h4>
                                            </div>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-transparent" style={{ backgroundColor: "rgba(59, 130, 246, 0.14)" }}>
                                                        <tr>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 uppercase">First Name</th>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 uppercase">Last Name</th>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 uppercase">Email</th>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 uppercase">Job</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-200">
                                                        {parsedCandidates.slice(0, 5).map((candidate, idx) => (
                                                            <tr key={idx}>
                                                                <td className="px-4 py-2 text-sm text-gray-900 font-medium">{candidate.firstName}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500">{candidate.lastName}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500">{candidate.email}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500">{candidate.jobTitle}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* Uploading Step */}
                    {step === 'uploading' && (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="h-12 w-12 animate-spin text-brand mb-4" />
                            <p className="text-lg font-medium text-gray-900 mb-2">Uploading Candidates...</p>
                            <p className="text-sm text-gray-500">Please wait while we process your data</p>
                        </div>
                    )}

                    {/* Result Step */}
                    {step === 'result' && uploadResult && (
                        <div className="space-y-6">
                            <div className={`text-center py-8 rounded-xl ${uploadResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                                {uploadResult.success ? (
                                    <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                                ) : (
                                    <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                                )}
                                <h3 className={`text-xl font-semibold mb-2 ${uploadResult.success ? 'text-green-800' : 'text-red-800'}`}>
                                    {uploadResult.success ? 'Upload Successful!' : 'Upload Failed'}
                                </h3>
                                <p className={`text-sm ${uploadResult.success ? 'text-green-600' : 'text-red-600'}`}>
                                    {uploadResult.message || uploadResult.error}
                                </p>
                            </div>

                            {uploadResult.data && (
                                <div className="grid grid-cols-4 gap-3">
                                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-gray-700">{uploadResult.data.total || 0}</p>
                                        <p className="text-sm text-gray-500">Total</p>
                                    </div>
                                    <div className="bg-green-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-green-700">{uploadResult.data.created || 0}</p>
                                        <p className="text-sm text-green-600">Created</p>
                                    </div>
                                    <div className="bg-red-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-red-700">{uploadResult.data.failed || 0}</p>
                                        <p className="text-sm text-red-600">Failed</p>
                                    </div>
                                    <div className="bg-yellow-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-yellow-700">{uploadResult.data.skipped || 0}</p>
                                        <p className="text-sm text-yellow-600">Skipped</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center space-x-3">
                        {step === 'preview' && (
                            <Button variant="outline" onClick={handleBack}>
                                Back
                            </Button>
                        )}
                        <Button className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors" onClick={handleClose}>
                            Cancel
                        </Button>
                    </div>

                    {step === 'preview' && (() => {
                        const errorCount = validationResult?.data?.failed || 0;
                        const validJobsCount = Math.max(0, parsedCandidates.length - errorCount - (skipDuplicates ? (validationResult?.data?.duplicates?.length || 0) : 0));
                        const hasNoValidJobs = validJobsCount <= 0;

                        return (
                            <div className="flex items-center space-x-3">
                                {hasNoValidJobs && (
                                    <span className="text-sm text-red-600">No valid candidates to upload</span>
                                )}
                                <Button
                                    variant="primary"
                                    icon={isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    onClick={handleUpload}
                                    disabled={isUploading || isValidating || hasNoValidJobs}
                                    className="disabled:bg-gray-400"
                                >
                                    {isUploading ? 'Uploading...' : `Upload ${validJobsCount} Candidates`}
                                </Button>
                            </div>
                        );
                    })()}

                    {step === 'result' && (
                        <Button
                            variant="primary"
                            onClick={handleClose}
                        >
                            Done
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CandidateBulkUploadModal;
